import { Roles } from '@common/types/user';
import { PHONE_REGEX_STRICT } from '@constants/schema';
import { IntlShape } from 'react-intl';
import * as Yup from 'yup';

export const AccountDetailsSchema = ({ formatMessage }: IntlShape) =>
    Yup.object({
        first_name: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.firstName', defaultMessage: 'First name' }) }
            )
        ),
        last_name: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.lastName', defaultMessage: 'Last name' }) }
            )
        ),
        email: Yup.string()
            .required(
                formatMessage(
                    {
                        id: 'schema.errors.required',
                        defaultMessage: '{field} is required',
                    },
                    { field: formatMessage({ id: 'common.email', defaultMessage: 'Email' }) }
                )
            )
            .email(formatMessage({ id: 'schema.errors.email.invalid', defaultMessage: 'Invalid email address' })),
        role: Yup.string().required(
            formatMessage(
                { id: 'schema.errors.required', defaultMessage: '{field} is required' },
                { field: formatMessage({ id: 'common.role', defaultMessage: 'Role' }) }
            )
        ),
        mobile_phone: Yup.string()
            .trim()
            .matches(
                PHONE_REGEX_STRICT,
                formatMessage({ id: 'schema.errors.phone.invalid', defaultMessage: 'Phone number not valid' })
            ),
        telephone: Yup.string()
            .trim()
            .matches(
                PHONE_REGEX_STRICT,
                formatMessage({ id: 'schema.errors.phone.invalid', defaultMessage: 'Phone number not valid' })
            ),
        fax: Yup.string()
            .trim()
            .matches(
                PHONE_REGEX_STRICT,
                formatMessage({ id: 'schema.errors.phone.invalid', defaultMessage: 'Phone number not valid' })
            ),
        company_name: Yup.string().when('role', {
            is: (role: string) => role === Roles.INSTALLER,
            then: (schema) =>
                schema.required(
                    formatMessage(
                        {
                            id: 'schema.errors.required',
                            defaultMessage: '{field} is required',
                        },
                        { field: formatMessage({ id: 'common.company', defaultMessage: 'Company name' }) }
                    )
                ),
            otherwise: (schema) => schema.notRequired(),
        }),
        job_title: Yup.string(),
        job_title_other: Yup.string().when('job_title', {
            is: (job_title: string) => job_title === 'other',
            then: (schema) =>
                schema.required(
                    formatMessage(
                        {
                            id: 'schema.errors.required',
                            defaultMessage: '{field} is required',
                        },
                        { field: formatMessage({ id: 'common.jobTitle', defaultMessage: 'Job title' }) }
                    )
                ),
            otherwise: (schema) => schema.notRequired(),
        }),
        address1: Yup.string(),
        address2: Yup.string(),
        country_code: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.country', defaultMessage: 'Country' }) }
            )
        ),
        state_code: Yup.string(),
        city: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.city', defaultMessage: 'City' }) }
            )
        ),
        receive_mailings_targeted_at_professionals: Yup.boolean().required(
            formatMessage({ id: 'account.details.mailings.required', defaultMessage: 'Mailing preference is required' })
        ),
        language: Yup.string().required(
            formatMessage(
                {
                    id: 'schema.errors.required',
                    defaultMessage: '{field} is required',
                },
                { field: formatMessage({ id: 'common.language', defaultMessage: 'Language' }) }
            )
        ),
        food_preference: Yup.string(),
    });
